import Shepherd from 'shepherd.js';
import { injectStepUI, markTourStartDate } from './TourHelpers';
import type { ShepherdTour, TrackableTour } from './TourHelpers';

export function createOEMDiagramsTour(): ShepherdTour {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: 'shepherd-theme-custom',
      scrollTo: true,
      cancelIcon: {
        enabled: true
      },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 6
    }
  });
  (tour as TrackableTour).__tourStartDate = null;

  tour.addStep({
    id: "step1",
    text: '<strong class="tour-step-header">OEM diagrams are now available on DealerNet, along with the corresponding numbered parts list.</strong><br>You can navigate around by clicking and dragging the diagram.',
    attachTo: {
      element: '.diagram-results-panel__sticky-diagram-content',
      on: "left-start"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        injectStepUI(this.getElement(), 1, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: () => {
          markTourStartDate(tour);
          tour.next();
        }
      }
    ]
  });

  tour.addStep({
    id: "step2",
    text: '<strong class="tour-step-header">Zoom the available diagrams.</strong><br>Use the plus (+) and minus (-) icons to zoom the diagram in and out.',
    attachTo: {
      element: '.diagram-results-panel__zoom-in, .diagram-results-panel__zoom-out',
      on: "bottom"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        injectStepUI(this.getElement(), 2, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: tour.next
      }
    ]
  });

  tour.addStep({
    id: "step3",
    text: '<strong class="tour-step-header">Reset zoom level.</strong><br>Click the reset zoom icon to restore the diagram to its default zoom level.',
    attachTo: {
      element: '.diagram-results-panel__reset-zoom',
      on: "bottom"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 3, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: tour.next
      }
    ]
  });

  tour.addStep({
    id: "step4",
    text: '<strong class="tour-step-header">The diagram defaults to portrait view for maximum viewing, but can be switched to landscape mode depending on screen size and orientation.</strong><br>DealerNet will remember your preference based on your last used orientation.',
    attachTo: {
      element: '.diagram-results-panel__orientation-btn',
      on: "bottom"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 4, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: tour.next
      }
    ]
  });

  tour.addStep({
    id: "step5",
    text: '<strong class="tour-step-header">You can also hide the entire diagram section.</strong><br>Click the "Hide Diagram" icon to collapse the diagram area, allowing you to view the parts list in full-screen mode.',
    attachTo: {
      element: '.diagram-results-panel__sticky-diagram-collapse-btn',
      on: "bottom-end"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 5, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: tour.next
      }
    ]
  });

  tour.addStep({
    id: "step6",
    text: '<strong class="tour-step-header">Use part reference numbers to find your part.</strong><br>Clicking a reference number within the diagram automatically scrolls to the corresponding part in the list, while clicking a reference number in the part list highlights the specific part within the diagram.',
    attachTo: {
      element: '.tpic__item-number',
      on: "bottom-end"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 6, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: tour.next
      }
    ]
  });

  tour.addStep({
    id: "step7",
    text: '<strong class="tour-step-header">Hide the selection panel.</strong><br>Once you have navigated to the appropriate component section, you can hide the panel to both increase the size of diagram and associated parts results.',
    attachTo: {
      element: '.diagram-page__selection-toggle',
      on: "right"
    },
    canClickTarget: false,
    scrollTo: false,
    when: {
      show() {
        injectStepUI(this.getElement(), 7, 8);
      }
    },
    buttons: [
      {
        text: "Next",
        action: tour.next
      }
    ]
  });

  tour.addStep({
    id: "step8",
    text: '<strong class="tour-step-header">Click and drag to resize the diagram window.</strong><br>Click and drag the corner of the diagram window to increase or decrease the viewing area.',
    attachTo: {
      element: '.diagram-results-panel__resize-handle-anchor',
      on: "bottom-end"
    },
    canClickTarget: false,
    scrollTo: true,
    when: {
      show() {
        injectStepUI(this.getElement(), 8, 8);
      }
    },
    buttons: [
      {
        text: "Close",
        action: tour.complete
      }
    ]
  });

  return tour;
}